import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import drawdownService, { DrawdownPacketRow } from '../../services/api/drawdownService';
import qsService, { ClaimableCertifiedBqLine, QsCertificateLine } from '../../services/api/qsService';
import tasksService, { Task } from '../../services/api/tasksService';
import taskMaterialsService, { TaskMaterial } from '../../services/api/taskMaterialsService';

export interface QsFieldData {
  projectId: string | null;
  projectName: string;
  lines: ClaimableCertifiedBqLine[];
  certificateLines: QsCertificateLine[];
  approvedTasks: Task[];
  approvedTaskMaterials: Record<string, TaskMaterial[]>;
  packets: DrawdownPacketRow[];
  packetsFailed: boolean;
  certificateLinesFailed: boolean;
  approvedTasksFailed: boolean;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export default function useQsFieldData(): QsFieldData {
  const { activeProjectId, projects } = useSelector((state: RootState) => state.projects);
  const [lines, setLines] = useState<ClaimableCertifiedBqLine[]>([]);
  const [certificateLines, setCertificateLines] = useState<QsCertificateLine[]>([]);
  const [approvedTasks, setApprovedTasks] = useState<Task[]>([]);
  const [approvedTaskMaterials, setApprovedTaskMaterials] = useState<Record<string, TaskMaterial[]>>({});
  const [packets, setPackets] = useState<DrawdownPacketRow[]>([]);
  const [packetsFailed, setPacketsFailed] = useState(false);
  const [certificateLinesFailed, setCertificateLinesFailed] = useState(false);
  const [approvedTasksFailed, setApprovedTasksFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const projectName = projects.find((project) => project.id === activeProjectId)?.name ?? 'Active project';

  useEffect(() => {
    if (!activeProjectId) {
      setLines([]);
      setCertificateLines([]);
      setApprovedTasks([]);
      setApprovedTaskMaterials({});
      setPackets([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [linesResult, certificateLinesResult, packetsResult] = await Promise.allSettled([
          qsService.getClaimableCertifiedBqLines(activeProjectId),
          qsService.getCertificateLines(activeProjectId),
          drawdownService.listPackets({ projectId: activeProjectId, limit: 25 }),
        ]);
        if (cancelled) return;

        if (linesResult.status === 'fulfilled') {
          setLines(linesResult.value);
        } else {
          setLines([]);
          setError(
            (linesResult.reason as any)?.response?.data?.message
            || (linesResult.reason as any)?.response?.data?.error
            || 'Failed to load claimable BQ lines'
          );
        }

        if (certificateLinesResult.status === 'fulfilled') {
          setCertificateLines(certificateLinesResult.value);
          setCertificateLinesFailed(false);
        } else {
          setCertificateLines([]);
          setCertificateLinesFailed(true);
        }

        if (packetsResult.status === 'fulfilled') {
          setPackets(Array.isArray(packetsResult.value?.packets) ? packetsResult.value.packets : []);
          setPacketsFailed(false);
        } else {
          setPackets([]);
          setPacketsFailed(true);
        }

        try {
          const taskResult = await tasksService.getTasks({
            projectId: activeProjectId,
            status: 'inspection_passed',
            limit: 100,
          });
          if (cancelled) return;
          setApprovedTasks(taskResult.tasks);
          const materialEntries = await Promise.all(
            taskResult.tasks.map(async (task) => {
              try {
                return [task.id, await taskMaterialsService.listForTask(task.id)] as const;
              } catch {
                return [task.id, []] as const;
              }
            })
          );
          if (cancelled) return;
          setApprovedTaskMaterials(Object.fromEntries(materialEntries));
          setApprovedTasksFailed(false);
        } catch {
          if (!cancelled) {
            setApprovedTasks([]);
            setApprovedTaskMaterials({});
            setApprovedTasksFailed(true);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [activeProjectId, reloadToken]);

  return {
    projectId: activeProjectId,
    projectName,
    lines,
    certificateLines,
    approvedTasks,
    approvedTaskMaterials,
    packets,
    packetsFailed,
    certificateLinesFailed,
    approvedTasksFailed,
    loading,
    error,
    reload: () => setReloadToken((token) => token + 1),
  };
}
